import React from "react";
import { useNavigate } from "react-router-dom";
import { useColors } from "../constants/Colors";
import { FaVideo, FaComment, FaHeart } from "react-icons/fa";

// Application like type
export interface ApplicationLike {
  application_id: number;
  user_id: number;
}

// Application type
export interface Application {
  application_id: number;
  user_id: number;
  room_id: number;
  type: "video" | "text";
  state: string;
  created_at: Date | string;
  text_application_answer: string | null;
  url?: string;
  application_likes?: ApplicationLike[];
  // For sent applications (flat info)
  flat_id?: number;
  flat_name?: string;
  flat_image?: string | null;
  // For received applications (applicant info)
  applicant_name?: string;
  applicant_image?: string | null;
}

interface ApplicationGridItemProps {
  application: Application;
  type: "sent" | "received";
  onVideoClick: (url: string) => void;
  onItemClick?: (applicationId: number) => void;
  currentUserId?: number;
  likesOverride?: ApplicationLike[];
  onToggleLike?: (applicationId: number) => void;
}

const ApplicationGridItem: React.FC<ApplicationGridItemProps> = ({
  application,
  type,
  onVideoClick,
  onItemClick,
  currentUserId,
  likesOverride,
  onToggleLike,
}) => {
  const colors = useColors();
  const navigate = useNavigate();

  const isDisabled =
    application.state === "declined" ||
    application.state === "retracted" ||
    application.state === "processing" ||
    application.state === "uploaded";

  const likes = likesOverride ?? application.application_likes ?? [];
  const hasUserLiked = Boolean(
    currentUserId && likes.some((like) => like.user_id === currentUserId),
  );
  const showStateTag =
    application.state === "declined" ||
    application.state === "retracted" ||
    application.state === "processing" ||
    application.state === "uploaded";

  const getStateColor = (state: string) => {
    switch (state) {
      case "accepted":
        return "#22c55e";
      case "declined":
      case "retracted":
        return "#ef4444";
      case "pending":
        return "#f59e0b";
      case "sent":
      case "uploaded":
      default:
        return colors.textNeutralMuted;
    }
  };

  const getStateLabel = (state: string) => {
    switch (state) {
      case "accepted":
        return "Accepted";
      case "declined":
        return "Declined";
      case "retracted":
        return "Retracted";
      case "pending":
        return "Pending";
      case "sent":
        return "Sent";
      case "uploaded":
        return "Processing";
      default:
        return state;
    }
  };

  const formatTime = (date: Date | string) => {
    return new Date(date).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleClick = () => {
    if (isDisabled) return;
    // If onItemClick is provided, use it to open carousel view
    if (onItemClick) {
      onItemClick(application.application_id);
      return;
    }
    // Fallback to video modal for backwards compatibility
    if (application.type === "video" && application.url) {
      onVideoClick(application.url);
    }
  };

  return (
    <div
      onClick={handleClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleClick();
        }
      }}
      tabIndex={isDisabled ? -1 : 0}
      role="button"
      aria-disabled={isDisabled}
      className={`relative overflow-hidden rounded-xl transition-all duration-200 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 w-full ${
        isDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
      }`}
      style={{
        aspectRatio: "3/4",
        backgroundColor:
          application.type === "text"
            ? colors.containerNeutralSolidSurface
            : colors.containerNeutralLightLowest,
        border:
          application.type === "text"
            ? `1px solid ${colors.containerNeutralLightLow}`
            : "none",
      }}
    >
      {/* Content */}
      {application.type === "text" ? (
        <div className="h-full p-3 flex flex-col">
          <div className="mt-10">
            <p
              className="text-sm leading-relaxed overflow-hidden line-clamp-[9] text-left"
              style={{ color: colors.textNeutralDefault }}
            >
              {application.text_application_answer || "No text provided."}
            </p>
          </div>
        </div>
      ) : (
        <div className="absolute inset-0">
          {/* Show applicant/flat image as large background */}
          {(() => {
            const previewImage =
              type === "sent"
                ? application.flat_image
                : application.applicant_image;
            const previewName =
              type === "sent"
                ? application.flat_name
                : application.applicant_name;

            // Check if image is a valid URL (not an ID or object key)
            const isValidImageUrl =
              previewImage &&
              (previewImage.startsWith("http") ||
                previewImage.startsWith("/") ||
                previewImage.startsWith("data:"));

            if (isValidImageUrl) {
              return (
                <img
                  src={previewImage}
                  alt={previewName || ""}
                  className="w-full h-full object-cover"
                />
              );
            }

            // Large fallback placeholder when no valid image URL
            return (
              <div
                className="w-full h-full flex items-center justify-center"
                style={{ backgroundColor: colors.containerNeutralLightMedium }}
              >
                <div
                  className="w-24 h-24 flex items-center justify-center text-4xl font-bold rounded-full"
                  style={{
                    backgroundColor: colors.accentColor,
                    color: colors.textNeutralOnPrimary,
                  }}
                >
                  {previewName?.charAt(0).toUpperCase() || "?"}
                </div>
              </div>
            );
          })()}
          {/* Subtle gradient overlay for video */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.3) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.2) 100%)",
            }}
          />
        </div>
      )}

      {/* Avatar and Name (top left) - clickable to navigate to flat/profile */}
      <button
        className="absolute top-2.5 left-2.5 z-10 flex items-center gap-2 max-w-[80%] hover:opacity-80 transition-opacity"
        onClick={(e) => {
          e.stopPropagation();
          if (type === "sent" && application.flat_id) {
            navigate(`/app/flat-view/${application.flat_id}`);
          } else if (type === "received" && application.user_id) {
            navigate(`/app/profile/${application.user_id}`);
          }
        }}
      >
        {(() => {
          const imageUrl =
            type === "sent"
              ? application.flat_image
              : application.applicant_image;
          const name =
            type === "sent"
              ? application.flat_name
              : application.applicant_name;
          const isVideo = application.type === "video";

          return (
            <>
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt=""
                  className="w-8 h-8 object-cover flex-shrink-0 rounded-full"
                />
              ) : (
                <div
                  className="w-8 h-8 flex items-center justify-center text-sm font-semibold flex-shrink-0 rounded-full"
                  style={{
                    backgroundColor: colors.accentColor,
                    color: colors.textNeutralOnPrimary,
                  }}
                >
                  {name?.charAt(0).toUpperCase() || "?"}
                </div>
              )}
              <span
                className="text-sm font-semibold truncate"
                style={{
                  color: isVideo ? "white" : colors.textNeutralDefault,
                }}
              >
                {name || "Unknown"}
              </span>
            </>
          );
        })()}
      </button>

      {/* State Tag - only show for declined, retracted, or processing states */}
      {showStateTag && (
        <div className="absolute top-2 right-2 z-10">
          <span
            className="px-2 py-0.5 rounded-full text-xs font-medium"
            style={{
              backgroundColor: `${getStateColor(application.state)}20`,
              color: getStateColor(application.state),
            }}
          >
            {getStateLabel(application.state)}
          </span>
        </div>
      )}

      {(likes.length > 0 || onToggleLike) && (
        <button
          onClick={(event) => {
            event.stopPropagation();
            onToggleLike?.(application.application_id);
          }}
          disabled={!onToggleLike}
          className={`absolute right-2 z-10 px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
            onToggleLike ? "cursor-pointer" : "cursor-default"
          }`}
          style={{
            top: showStateTag ? 34 : 8,
            backgroundColor: hasUserLiked ? "#ef4444" : "rgba(0, 0, 0, 0.5)",
            color: "white",
          }}
          title={
            onToggleLike
              ? hasUserLiked
                ? "Unlike application"
                : "Like application"
              : "Application likes"
          }
        >
          <FaHeart className={hasUserLiked ? "" : "opacity-80"} size={10} />
          <span>{likes.length}</span>
        </button>
      )}

      {/* Type Icon */}
      <div className="absolute bottom-2 left-2 z-10">
        <div
          className="rounded-full p-1.5 flex items-center justify-center"
          style={{
            backgroundColor:
              application.type === "video" ? "rgba(0,0,0,0.6)" : "transparent",
          }}
        >
          {application.type === "video" ? (
            <FaVideo className="text-white" size={12} />
          ) : (
            <FaComment size={12} style={{ color: colors.textNeutralDefault }} />
          )}
        </div>
      </div>

      {/* Timestamp */}
      <div className="absolute bottom-2 right-2 z-10">
        <span
          className="px-2 py-0.5 rounded-full text-xs"
          style={{
            backgroundColor: "rgba(0,0,0,0.5)",
            color: "white",
          }}
        >
          {formatTime(application.created_at)}
        </span>
      </div>
    </div>
  );
};

export default ApplicationGridItem;
